import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { i18n as I18nApi } from 'i18next'
import dayjs from 'dayjs'
import { css } from '@emotion/core'

import { OperationLogBatchSummaryType, OperationLogChangeEntry, OperationLogGroupedItemType } from '@/models/operation-log'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useAppDispatch, useAppSelector } from '@/features/hooks'
import { fetchOperationLogBatchDetail } from '@/features/operation-log/operation-log-thunks'
import {
  getOperationLogFieldLabelKey,
  getSubjectTypeByFieldKey,
  RESERVATION_NOTE_MEMO_FIELD_ORDER,
  subjectTypeFromItemLabel,
} from '@/constants/operation-log-field-labels'
import { getModuleLabel, normalizeOperationLogFeatureValue, OPERATION_LOG_MODULE_I18N_KEY } from '@/constants/operation-log'
import { getSummaryDisplay } from '@/utils/operation-log-summary'
import { mergeSalesUpdateGroupedItems, OPERATION_LOG_SALES_UPDATE_GROUP_LABEL } from '@/utils/operation-log-sales-display'
import {
  operationLogCellEllipsisStyle,
  operationLogTdDatetimeStyle,
  operationLogTdEllipsisStyle,
  operationLogTdNowrapStyle,
} from '@/components/organisms/operation-log/table-cell-styles'

/** itemLabel のキーにドットが含まれるため、フラットオブジェクトを bundle から直接参照する */
const resolveOperationLogItemLabel = (i18n: I18nApi, itemLabel: string): string => {
  const trimmed = (itemLabel ?? '').toString().trim()
  if (!trimmed) return trimmed
  /** BE が item_label に「スラッグのみ」ではなく「csv_download.created」等を付けると normalize(全体) が一致せず末尾 return trimmed になり英語のまま残る */
  const headSeg = trimmed.split(/[.\s\u3000]/)[0] ?? ''
  if (
    normalizeOperationLogFeatureValue(trimmed) === 'csv_download' ||
    normalizeOperationLogFeatureValue(headSeg) === 'csv_download'
  ) {
    return i18n.t('OperationLog.module.csv_download')
  }

  const root = i18n.getResourceBundle(i18n.language, 'translation') as Record<string, unknown> | undefined
  const opLog = root?.OperationLog as Record<string, unknown> | undefined
  const map = opLog?.itemLabel as Record<string, string> | undefined
  const mapped = map?.[trimmed]
  if (typeof mapped === 'string' && mapped !== '') return mapped

  /** 多言語で itemLabel が未整備な場合でも、最低限 ja 辞書で表示崩れを防ぐ */
  const jaRoot = i18n.getResourceBundle('ja', 'translation') as Record<string, unknown> | undefined
  const jaOpLog = jaRoot?.OperationLog as Record<string, unknown> | undefined
  const jaMap = jaOpLog?.itemLabel as Record<string, string> | undefined
  const jaMapped = jaMap?.[trimmed]
  if (typeof jaMapped === 'string' && jaMapped !== '') return jaMapped

  const head = trimmed.split(/[.\s\u3000]/)[0] ?? ''
  const mod = normalizeOperationLogFeatureValue(head) ?? normalizeOperationLogFeatureValue(trimmed)
  if (mod) {
    const key = OPERATION_LOG_MODULE_I18N_KEY[mod]
    if (key != null && i18n.exists(key)) return i18n.t(key)
  }
  const compactHead = head.replace(/[_\s-]/g, '').toLowerCase()
  if (compactHead === 'csvdownload' || compactHead.startsWith('csvdownload')) {
    const key = OPERATION_LOG_MODULE_I18N_KEY.csv_download
    if (i18n.exists(key)) return i18n.t(key)
  }

  return trimmed
}

export type OperationLogRowProps = {
  item: OperationLogBatchSummaryType
  isOpen: boolean
  onToggle: () => void
}

/** 詳細の変更ラベル用に module から subject_type を推定。API は常に value（小文字スネーク）のみ返すためキーもそれに合わせる。 */
const subjectTypeFromModule = (module: string): string => {
  const map: Record<string, string> = {
    sales: 'SalesSubject',
    accommodation: 'Reservation',
    room: 'GuestRoomAssign',
    setting: 'Reservation',
    space: 'Space',
    mobile_request: 'MobileRequestOrder',
    csv_download: 'Reservation',
  }
  return map[module] ?? 'Reservation'
}

/* mock 相当: blue-100/800, green-100/800, red-100/800 */
const OPERATION_BADGE_COLORS: Record<'update' | 'create' | 'delete', { bg: string; color: string }> = {
  update: { bg: '#DBEAFE', color: '#1E40AF' },
  create: { bg: '#DCFCE7', color: '#166534' },
  delete: { bg: '#FEE2E2', color: '#991B1B' },
}

const operationBadgeStyle = (kind: 'update' | 'create' | 'delete' | null) =>
  kind && OPERATION_BADGE_COLORS[kind]
    ? css({
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 'bold',
        backgroundColor: OPERATION_BADGE_COLORS[kind].bg,
        color: OPERATION_BADGE_COLORS[kind].color,
      })
    : css({})

/** 旧レスの category や運用ノイズになりやすい hotel_id は除外。memo は Reservation 等の冗長分のみ除外し、ReservationNote の memo は表示する */
const SKIP_OPERATION_LOG_FIELDS_ALWAYS = new Set(['category', 'hotel_id'])

function shouldSkipOperationLogField(field: string, subject: string): boolean {
  const f = field.trim()
  if (SKIP_OPERATION_LOG_FIELDS_ALWAYS.has(f)) return true
  if (f === 'memo' && subject !== 'ReservationNote') return true
  return false
}

function isReservationMemoField(field: string): boolean {
  return (RESERVATION_NOTE_MEMO_FIELD_ORDER as readonly string[]).includes(field)
}

function sortReservationMemoFields(a: { field: string }, b: { field: string }): number {
  const order = RESERVATION_NOTE_MEMO_FIELD_ORDER as readonly string[]
  const ia = order.indexOf(a.field)
  const ib = order.indexOf(b.field)
  if (ia === -1 && ib === -1) return 0
  if (ia === -1) return 1
  if (ib === -1) return -1
  return ia - ib
}

/** 更新APIが集約した memo を、作成時と同様カテゴリ別に並べるためのキー（JSON / オブジェクトの両方） */
const RESERVATION_NOTE_STRUCTURED_MEMO_KEYS = ['memo_front', 'memo_cleaning', 'memo_meal'] as const

function parseStructuredReservationNoteMemo(v: unknown): Record<string, string> | null {
  let obj: Record<string, unknown> | null = null
  if (typeof v === 'string') {
    const s = v.trim()
    if (!s || s === '-') return null
    if (!s.startsWith('{')) return null
    try {
      obj = JSON.parse(s) as Record<string, unknown>
    } catch {
      return null
    }
  } else if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
    obj = v as Record<string, unknown>
  } else {
    return null
  }
  const out: Record<string, string> = {}
  for (const k of RESERVATION_NOTE_STRUCTURED_MEMO_KEYS) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = String(obj[k] ?? '')
  }
  return Object.keys(out).length > 0 ? out : null
}

/**
 * field=memo かつ old/new のいずれかが { memo_front, ... } 形式なら、カテゴリごとの変更行に展開する。
 * プレーン文字列のみのときはそのまま（1行「メモ：…」表示）。
 */
function expandReservationNoteMemoChanges(list: OperationLogChangeEntry[], subject: string): OperationLogChangeEntry[] {
  if (subject !== 'ReservationNote' || list.length === 0) return list

  const idx = list.findIndex(c => (c.field ?? '').trim() === 'memo')
  if (idx === -1) return list

  const memo = list[idx]
  const oldM = parseStructuredReservationNoteMemo(memo.old)
  const newM = parseStructuredReservationNoteMemo(memo.new)
  if (oldM === null && newM === null) return list

  const keySet = new Set<string>()
  for (const k of RESERVATION_NOTE_STRUCTURED_MEMO_KEYS) {
    if (oldM && Object.prototype.hasOwnProperty.call(oldM, k)) keySet.add(k)
    if (newM && Object.prototype.hasOwnProperty.call(newM, k)) keySet.add(k)
  }
  if (keySet.size === 0) return list

  const expanded: OperationLogChangeEntry[] = []
  for (const k of RESERVATION_NOTE_STRUCTURED_MEMO_KEYS) {
    if (!keySet.has(k)) continue
    expanded.push({
      field: k,
      old: oldM && Object.prototype.hasOwnProperty.call(oldM, k) ? oldM[k] : '',
      new: newM && Object.prototype.hasOwnProperty.call(newM, k) ? newM[k] : '',
      field_label: null,
    })
  }
  return [...list.slice(0, idx), ...expanded, ...list.slice(idx + 1)]
}

function isBooleanLikeOperationLogField(field: string): boolean {
  const f = (field ?? '').trim()
  if (!f) return false
  if (f.startsWith('is_') || f.startsWith('has_')) return true
  return /^is[A-Z]/.test(f)
}

/** 表示用の値整形。null / 文字列 "null" は '-'。真偽値は Yes/No。10桁 unix は日時に整形 */
function formatOpLogChangeDisplayValue(v: unknown, field: string, t: (key: string) => string): string {
  if (v === null || v === undefined) return '-'
  if (typeof v === 'boolean') return v ? t('Yes') : t('No')
  if (typeof v === 'number') {
    if (isBooleanLikeOperationLogField(field) && (v === 0 || v === 1)) return v === 1 ? t('Yes') : t('No')
    if (Number.isInteger(v) && v >= 1_000_000_000 && v < 10_000_000_000) {
      const d = dayjs.unix(v)
      if (d.isValid()) return d.format('YYYY-MM-DD HH:mm:ss')
    }
    return String(v)
  }
  if (typeof v === 'string') {
    const s = v.trim()
    if (s === '' || s === 'null') return '-'
    if (/^\d{10}$/.test(s)) {
      const d = dayjs.unix(Number(s))
      if (d.isValid()) return d.format('YYYY-MM-DD HH:mm:ss')
    }
    if (isBooleanLikeOperationLogField(field) && (s === '0' || s === '1')) return s === '1' ? t('Yes') : t('No')
    if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
      const d = dayjs(s)
      if (d.isValid()) return d.format('YYYY-MM-DD HH:mm:ss')
    }
    return s
  }
  return String(v)
}

const OPERATION_LOG_DATETIME_DISPLAY_RE = /^\d{4}[-/]\d{2}[-/]\d{2} \d{2}:\d{2}:\d{2}$/

function isOperationLogDatetimeDisplayValue(value: string): boolean {
  return OPERATION_LOG_DATETIME_DISPLAY_RE.test(value.trim())
}

type AugmentedChange = OperationLogChangeEntry & { labelKey: string | null }

type GroupedDisplayItem = {
  id?: number
  item_label?: string
  changes?: OperationLogChangeEntry[]
}

/**
 * 予約メモは activity 単位で分かれて返ることがあるため、同一 item_label は1ブロックにまとめる。
 * 例: ReservationNote 作成 + memo_front / memo_cleaning を1つの見出し配下に表示。
 */
function mergeGroupedItemsForDisplay(items: GroupedDisplayItem[]): GroupedDisplayItem[] {
  const merged: GroupedDisplayItem[] = []
  for (const item of items) {
    const label = (item.item_label ?? '').toString()
    const subject = subjectTypeFromItemLabel(label)
    const isReservationNote = subject === 'ReservationNote'
    const prev = merged[merged.length - 1]
    if (isReservationNote && prev && prev.item_label === label) {
      prev.changes = [...(prev.changes ?? []), ...(item.changes ?? [])]
      continue
    }
    merged.push({
      ...item,
      changes: [...(item.changes ?? [])],
    })
  }
  return merged
}

export const OperationLogRow: React.FC<OperationLogRowProps> = ({ item, isOpen, onToggle }) => {
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const dispatch = useAppDispatch()
  const batchUuid = item.batch_uuid
  const detail = useAppSelector(s => s.operationLog.detailsByBatchUuid[batchUuid])
  const detailInFlight = useAppSelector(s => s.operationLog.detailInFlight[batchUuid])
  const featureKey = (item.feature_slug ?? item.module ?? '').toString()
  const subjectType = subjectTypeFromModule(featureKey)
  const { actionLabel: operationLabel, fullLabel, badgeKind: operationKind } = getSummaryDisplay(item, t)
  const detailPanelId = `oplog-detail-${batchUuid}`

  useEffect(() => {
    if (!isOpen) return
    if (detail !== undefined) return
    if (detailInFlight) return
    dispatch(fetchOperationLogBatchDetail({ batchUuid }))
      .unwrap()
      .catch(err => errorHandler(err))
  }, [isOpen, batchUuid, detail, detailInFlight, dispatch, errorHandler])

  const renderChanges = (
    changes: OperationLogChangeEntry[] | undefined,
    subject: string,
    tr: (key: string) => string,
    opts?: { omitReservationId?: boolean },
  ): React.ReactNode => {
    const filtered = (changes ?? []).filter(c => !shouldSkipOperationLogField((c.field ?? '').toString(), subject))
    const list = expandReservationNoteMemoChanges(filtered, subject)
    const withLabel: AugmentedChange[] = list.map(c => {
      const fieldKey = (c.field ?? '').toString().trim()
      let labelKey = getOperationLogFieldLabelKey(subject, fieldKey)
      if (labelKey == null) {
        const subjectByField = getSubjectTypeByFieldKey(fieldKey)
        if (subjectByField) labelKey = getOperationLogFieldLabelKey(subjectByField, fieldKey)
      }
      return { ...c, field: fieldKey, labelKey }
    })

    const omitReservationId = Boolean(opts?.omitReservationId)
    const visible = omitReservationId ? withLabel.filter(c => c.field !== 'reservation_id') : withLabel

    if (!visible.length) {
      if (omitReservationId && withLabel.length > 0 && withLabel.every(c => c.field === 'reservation_id')) {
        return null
      }
      return (
        <div css={detailChangeCardStyle}>
          <span css={detailNoChangesStyle}>{tr('Operation log no changes')}</span>
        </div>
      )
    }

    const memoRows = visible.filter(c => isReservationMemoField(c.field)).sort(sortReservationMemoFields)
    const cardRows = visible.filter(c => !isReservationMemoField(c.field))

    const memoLabel = (c: AugmentedChange): string => {
      if (c.labelKey != null && c.labelKey !== '') return tr(c.labelKey)
      const raw = c.field_label
      if (raw != null && String(raw).trim() !== '') return String(raw).trim()
      return c.field
    }

    const renderChangeValue = (v: unknown, field: string, variant: 'old' | 'new') => {
      const text = formatOpLogChangeDisplayValue(v, field, tr)
      const isDatetime = isOperationLogDatetimeDisplayValue(text)
      const textStyle = variant === 'old' ? detailChangeOldTextStyle : detailChangeNewTextStyle
      const boxStyle = variant === 'old' ? detailChangeOldBoxStyle : detailChangeNewBoxStyle
      return (
        <div css={[boxStyle, isDatetime && detailChangeDatetimeBoxStyle]}>
          <p css={[textStyle, isDatetime && detailChangeDatetimeTextStyle]}>{text}</p>
        </div>
      )
    }

    return (
      <>
        {memoRows.map((c, i) => (
          <div key={`memo-card-${c.field}-${i}`} css={detailChangeCardStyle}>
            <div css={detailChangeFieldLabelStyle}>{memoLabel(c)}</div>
            <div css={detailChangeValuesStyle}>
              {renderChangeValue(c.old, c.field, 'old')}
              <div css={detailChangeArrowStyle}>→</div>
              {renderChangeValue(c.new, c.field, 'new')}
            </div>
          </div>
        ))}
        {cardRows.map((c, i) => (
          <div key={`card-${c.field}-${i}`} css={detailChangeCardStyle}>
            <div css={detailChangeFieldLabelStyle}>
              {c.labelKey != null && c.labelKey !== ''
                ? tr(c.labelKey)
                : c.field_label != null && String(c.field_label).trim() !== ''
                ? String(c.field_label).trim()
                : c.field}
            </div>
            <div css={detailChangeValuesStyle}>
              {renderChangeValue(c.old, c.field, 'old')}
              <div css={detailChangeArrowStyle}>→</div>
              {renderChangeValue(c.new, c.field, 'new')}
            </div>
          </div>
        ))}
      </>
    )
  }

  return (
    <>
      <tr css={{ color: '#272727' }}>
        <td css={[operationLogTdNowrapStyle, { paddingLeft: 28, width: '5%' }]}>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-controls={isOpen ? detailPanelId : undefined}
            aria-label={t('OperationLog.toggleRowDetail')}
            onClick={onToggle}
            css={expandRowButtonStyle}
          >
            <img
              src={require('@/static/images/arrow_right.svg')}
              alt=""
              aria-hidden
              css={{
                width: 32,
                height: 32,
                display: 'block',
                transform: `rotate(${isOpen ? -90 : 90}deg)`,
                transition: 'all 300ms',
              }}
            />
          </button>
        </td>
        <td css={[operationLogTdDatetimeStyle, { width: '15%' }]}>{item.datetime ?? '-'}</td>
        <td css={[operationLogTdNowrapStyle, { width: '10%' }]}>
          <span css={operationBadgeStyle(operationKind)}>{operationLabel}</span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '10%' }]}>
          <span css={operationLogCellEllipsisStyle} title={getModuleLabel(featureKey, t) || featureKey || '-'}>
            {getModuleLabel(featureKey, t) || featureKey || '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '25%' }]}>
          <span css={operationLogCellEllipsisStyle} title={fullLabel}>
            {fullLabel || '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '18%' }]}>
          <span css={operationLogCellEllipsisStyle} title={item.account ?? '-'}>
            {item.account ?? '-'}
          </span>
        </td>
        <td css={[operationLogTdEllipsisStyle, { width: '17%', paddingRight: 28 }]}>
          <span css={operationLogCellEllipsisStyle} title={item.staff ?? '-'}>
            {item.staff ?? '-'}
          </span>
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td id={detailPanelId} colSpan={7} css={detailTdStyle}>
            {detail === undefined ? (
              <div css={loadingStyle}>{t('Loading')}...</div>
            ) : detail === null ? (
              <div css={errorStyle}>{t('Operation log not found')}</div>
            ) : (
              <div css={detailOuterStyle}>
                {/* 4.2: reservation_id / guest_name はレスポンスルートに 1 つ */}
                {(detail.reservation_id || detail.guest_name) && (
                  <div css={detailInfoCardStyle}>
                    <div css={detailInfoRowStyle}>
                      {detail.reservation_id && (
                        <div>
                          <span css={detailInfoLabelStyle}>{t('Reservation ID')}</span>
                          <p css={detailInfoValueStyle}>{detail.reservation_id}</p>
                        </div>
                      )}
                      {detail.guest_name && (
                        <div>
                          <span css={detailInfoLabelStyle}>{t('Guest name')}</span>
                          <p css={detailInfoValueStyle}>{detail.guest_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* grouped があれば items（item_label + changes）、なければ activities をフラット表示 */}
                {detail.grouped?.length ? (
                  detail.grouped.map((grp, grpIdx) => {
                    const mergedMemo = mergeGroupedItemsForDisplay(grp.items)
                    const displayItems = (
                      String(grp.group_label ?? '').trim() === OPERATION_LOG_SALES_UPDATE_GROUP_LABEL
                        ? mergeSalesUpdateGroupedItems(mergedMemo)
                        : mergedMemo
                    ) as OperationLogGroupedItemType[]
                    const itemNodes = displayItems.map((item, itemIdx) => {
                      const changesNode = renderChanges(item.changes, subjectTypeFromItemLabel(item.item_label ?? '') ?? subjectType, t, {
                        omitReservationId: Boolean(detail.reservation_id),
                      })
                      if (changesNode == null) return null
                      return (
                        <div key={item.id ?? itemIdx} css={detailItemBlockStyle}>
                          {item.item_label && <div css={detailItemLabelStyle}>{resolveOperationLogItemLabel(i18n, item.item_label)}</div>}
                          <div css={detailChangesSectionStyle}>{changesNode}</div>
                        </div>
                      )
                    })
                    if (!itemNodes.some(n => n != null)) return null
                    return (
                      <div key={grpIdx} css={activityBlockStyle}>
                        {itemNodes}
                      </div>
                    )
                  })
                ) : detail.activities?.length ? (
                  detail.activities.map((activity, activityIdx) => {
                    const changesNode = renderChanges(activity.changes, subjectType, t, {
                      omitReservationId: Boolean(detail.reservation_id),
                    })
                    if (changesNode == null) return null
                    return (
                      <div key={activity.id ?? activityIdx} css={activityBlockStyle}>
                        <div css={detailChangesSectionStyle}>{changesNode}</div>
                      </div>
                    )
                  })
                ) : (
                  <div css={detailChangeCardStyle}>
                    <span css={detailNoChangesStyle}>{t('Operation log no changes')}</span>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

const expandRowButtonStyle = css({
  border: 'none',
  background: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  lineHeight: 0,
  verticalAlign: 'middle',
})

const detailTdStyle = css({
  padding: '24px 28px',
  backgroundColor: 'rgba(0,0,0,0.03)',
  verticalAlign: 'top',
  overflowX: 'hidden',
  maxWidth: 0,
})

const loadingStyle = css({
  padding: 28,
  backgroundColor: '#fff',
  borderRadius: 12,
  color: '#676767',
  fontSize: 12,
})

const errorStyle = css({
  padding: 28,
  backgroundColor: '#fff',
  borderRadius: 12,
  color: '#c00',
  fontSize: 12,
})

/* 詳細エリア（mock: space-y-4） */
const detailOuterStyle = css({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 16,
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
})

/* 1 activity 分のブロック（複数あるときの区切り） */
const activityBlockStyle = css({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 12,
})

const detailItemBlockStyle = css({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 8,
})

const detailItemLabelStyle = css({
  fontSize: 13,
  fontWeight: 500,
  color: '#6B7280',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

/* 予約ID・宿泊者名カード（mock: rounded-lg border bg-white p-4） */
const detailInfoCardStyle = css({
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  padding: 16,
})

const detailInfoRowStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 24,
  minWidth: 0,
  '> div': {
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
  },
})

const detailInfoLabelStyle = css({
  display: 'block',
  fontSize: 12,
  fontWeight: 500,
  color: '#6B7280',
})

const detailInfoValueStyle = css({
  marginTop: 4,
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: '#272727',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

/* 変更内容セクション（mock: space-y-3） */
const detailChangesSectionStyle = css({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 12,
})

/* 変更1件のカード（mock: rounded-lg border bg-white p-4, flex items-center gap-3） */
const detailChangeCardStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  borderRadius: 8,
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  padding: 16,
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
})

const detailNoChangesStyle = css({
  fontSize: 14,
  color: '#6B7280',
})

const detailChangeFieldLabelStyle = css({
  /* OLD/NEW の開始位置を揃えるために最小限の固定幅だけ持たせる */
  width: 128,
  flex: '0 0 128px',
  fontSize: 14,
  fontWeight: 600,
  color: '#272727',
})

const detailChangeValuesStyle = css({
  display: 'grid',
  flex: 1,
  minWidth: 0,
  gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
  alignItems: 'center',
  gap: 12,
  overflow: 'hidden',
})

/* 旧値ボックス（mock: bg-red-50, line-through） */
const detailChangeOldBoxStyle = css({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  borderRadius: 6,
  backgroundColor: '#FEF2F2',
  padding: '12px 16px',
})

const detailChangeOldTextStyle = css({
  margin: 0,
  fontSize: 14,
  color: '#6B7280',
  textDecoration: 'line-through',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

const detailChangeArrowStyle = css({
  fontSize: 18,
  color: '#6B7280',
})

/* 新値ボックス（mock: bg-green-50, font-medium） */
const detailChangeNewBoxStyle = css({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  borderRadius: 6,
  backgroundColor: '#F0FDF4',
  padding: '12px 16px',
})

const detailChangeNewTextStyle = css({
  margin: 0,
  fontSize: 14,
  fontWeight: 500,
  color: '#272727',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
})

const detailChangeDatetimeBoxStyle = css({
  overflow: 'visible',
  minWidth: 'max-content',
})

const detailChangeDatetimeTextStyle = css({
  overflow: 'visible',
  textOverflow: 'clip',
  whiteSpace: 'nowrap',
})
